import { useState } from "react";
import {
  Phone,
  PhoneOutgoing,
  ChevronDown,
  ChevronUp,
  Mic,
  FileText,
  Clock,
  XCircle,
} from "lucide-react";
import { RelativeTime } from "../shared/RelativeTime";
import type { CallRecord } from "./useCallLog";

interface CallEntryProps {
  call: CallRecord;
}

export function CallEntry({ call }: CallEntryProps) {
  const [expanded, setExpanded] = useState(false);

  const isInbound = call.direction === "inbound";
  const hasRecording = !!call.recording_url;
  const hasTranscript = !!call.transcript;
  const duration = formatDuration(call.duration_seconds);
  const isCompleted = call.twilio_status === "completed";

  return (
    <div
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        marginBottom: 8,
        overflow: "hidden",
      }}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          width: "100%",
          padding: "12px 16px",
          border: "none",
          background: expanded ? "#f9fafb" : "white",
          cursor: "pointer",
          textAlign: "left",
          fontSize: 14,
        }}
      >
        {isInbound ? (
          <Phone size={16} style={{ color: "#2563eb", flexShrink: 0 }} />
        ) : (
          <PhoneOutgoing size={16} style={{ color: "#7c3aed", flexShrink: 0 }} />
        )}

        <span style={{ fontWeight: 500, color: "#111827" }}>
          {isInbound ? "Inbound" : "Outbound"}
        </span>
        <span style={{ color: "#6b7280", fontSize: 13 }}>
          {isInbound ? call.from_number : call.to_number}
        </span>

        <span style={{ flex: 1 }} />

        {!isCompleted && (
          <span
            style={{
              fontSize: 11,
              padding: "2px 6px",
              borderRadius: 4,
              backgroundColor: "#fef3c7",
              color: "#92400e",
            }}
          >
            {call.twilio_status}
          </span>
        )}

        {call.duration_seconds > 0 && (
          <span style={{ fontSize: 12, color: "#6b7280" }}>
            <Clock size={12} style={{ verticalAlign: "middle", marginRight: 2 }} />
            {duration}
          </span>
        )}

        {hasRecording && <Mic size={14} style={{ color: "#059669", flexShrink: 0 }} />}
        {hasTranscript && <FileText size={14} style={{ color: "#2563eb", flexShrink: 0 }} />}

        <span style={{ fontSize: 12, color: "#9ca3af", whiteSpace: "nowrap" }}>
          <RelativeTime date={call.created_at} />
        </span>

        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {expanded && (
        <div
          style={{
            padding: "12px 16px",
            borderTop: "1px solid #f3f4f6",
            backgroundColor: "#fafafa",
          }}
        >
          {hasRecording && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: "#374151", marginBottom: 4 }}>
                Recording
              </div>
              <audio controls preload="metadata" style={{ width: "100%", height: 36 }} src={call.recording_url!}>
                Your browser does not support audio playback.
              </audio>
            </div>
          )}

          {hasTranscript && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: "#374151", marginBottom: 4 }}>
                Transcript
              </div>
              <div
                style={{
                  fontSize: 13,
                  lineHeight: 1.6,
                  color: "#374151",
                  backgroundColor: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: 6,
                  padding: "8px 12px",
                  maxHeight: 200,
                  overflowY: "auto",
                  whiteSpace: "pre-wrap",
                }}
              >
                {call.transcript}
              </div>
            </div>
          )}

          {call.transcription_status === "pending" && (
            <div style={{ fontSize: 12, color: "#d97706" }}>⏳ Transcription pending…</div>
          )}
          {call.transcription_status === "processing" && (
            <div style={{ fontSize: 12, color: "#2563eb" }}>🔄 Transcription in progress…</div>
          )}
          {call.transcription_status === "failed" && (
            <div style={{ fontSize: 12, color: "#dc2626" }}>
              <XCircle size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />
              Transcription failed
            </div>
          )}

          {!hasRecording && !hasTranscript && (
            <div style={{ fontSize: 12, color: "#9ca3af" }}>
              No recording available for this call.
            </div>
          )}

          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 8, display: "flex", gap: 16 }}>
            <span>SID: {call.twilio_call_sid}</span>
            <span>Status: {call.twilio_status}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}
